import { useIssueReportOffer } from '@/hooks/useIssueReportOffer';
import { PUBLIC_BODY_CLASS, PUBLIC_BODY_COLOR } from '../public-text';
import { fillOfferLine, fillOfferTokens, usesOfferTokens } from '@/lib/utils/offer-tokens.utils';
import { DEFAULT_CARD_GRID_IMAGES, DEFAULT_CARD_GRID_HEADER_IMAGE, DEFAULT_CARD_GRID_FILLER_IMAGE, CARD_ICON_BY_KEY, imageOrDefault } from '../default-images';
import { SectionCta, type SectionOfferCta } from '../SectionCta';
import type { SectionRendererProps } from '../section-renderer.types';

interface CardItem {
  title: string;
  body: string;
  icon?: string;
  image?: string;
  highlighted?: boolean;
}

interface CardGridContent {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  // Due Diligence Platform's pricing/benefit bands (docs/specs/features/
  // public-pages/02-design.md §23, FR-153/FR-154) - intro paragraphs above
  // the card grid, the same paragraph-array idiom as hero/cta-banner/
  // mission-split. Every other card-grid usage leaves this unset.
  body?: string[];
  // Due Diligence Platform's pricing/benefit bands ("Your purchase
  // includes:", "Your package includes:") - a short label directly above the
  // card list, distinct from eyebrow/title/subtitle/body. Every other
  // card-grid usage leaves this unset.
  cardsHeading?: string;
  cards: CardItem[];
  ctaLabel?: string;
  ctaUrl?: string;
  // Due Diligence Platform's pricing/benefit bands (§23, FR-153/FR-154) - a
  // short price line and a closing tagline, rendered between the cards and
  // the CTA. Both unset for every other card-grid usage.
  priceLine?: string;
  closingLine?: string;
  // MYN-285 (FR-202): marks this band's CTA as the Issue Report buy action, so
  // it follows the public offer ("Coming Soon" while no plan is active). Text
  // fields may carry the offer's `{{issueReportPrice}}` / `{{issueReportAccessLength}}`
  // placeholders. Both unset for every other card-grid usage.
  offerCta?: SectionOfferCta;
  // MYN-301 (FR-217): opens this band's CTA in a new tab. Set on the Due
  // Diligence page's Seed Financing band only; every other card-grid usage
  // leaves it unset and keeps today's same-tab navigation.
  ctaOpensInNewTab?: boolean;
}

/**
 * Grid column count at the `lg` breakpoint, keyed by section `key` - Home's
 * "fundraising-advantage" is 2x2 in Figma, not a single row of 4. Benefits'
 * 6-card "membership-benefits" ("Free Membership Benefits") is 3-over-3 per
 * the client docx, not the 4-column default (which would split 6 cards
 * unevenly as 4+2).
 */
const LG_COLUMNS: Partial<Record<string, 2 | 3 | 5>> = {
  'fundraising-advantage': 2,
  'membership-benefits': 3,
  // trusted_p_ch.docx: "all of the trusted partner benefits need to be
  // visible at a glance... listed like the membership benefits on the
  // Benefits page" - same 3-column treatment, not the horizontally-scrolling
  // carousel FR-143/`02-design.md` §22 originally called for (CORRECTED
  // 2026-10-08, during implementation - see this file's own header comment).
  'partners-benefits': 3,
  // Fundraising Support's "exclusive workspaces" band: two cards at two
  // fifths each beside a one-fifth image (FR-308, A-345).
  'exclusive-workspaces': 5,
};

const LG_COLUMNS_CLASS: Record<2 | 3 | 4 | 5, string> = {
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
};

/**
 * Section background, keyed by `section.key` - every other card-grid usage
 * sits on plain white, but Figma's own "Parters Benefits" node uses a
 * cream-to-mint gradient (`from-[#f9f8f3] to-[#f5fdf9]`), the same pairing
 * already used for the Apply-form panel elsewhere on this page - not pure
 * white, confirmed by direct comparison against the live Figma file.
 *
 * Due Diligence Platform's "seed-financing-package" band reuses this same
 * gradient (MYN-269, explicit instruction) so the page alternates section
 * backgrounds the way Home does, without touching card text colors - the
 * gradient is light enough that the existing navy/teal card text stays
 * legible with no further changes.
 *
 * "exclusive-workspaces" is a genuinely dark band (MYN-269, explicit
 * instruction) - `bg-dkgreen`, the same dark token `cta-banner`'s
 * `ai-due-diligence`/Benefits-stats-bar treatment already uses elsewhere on
 * these pages, picked instead of the stats-bar's navy for variety between
 * the two dark bands on this page. `GridCard`'s own white card surface is
 * untouched, so card title/body stay perfectly legible without any card-level
 * change - only the section's own floating eyebrow/title/subtitle (see
 * `HEADER_COLOR_OVERRIDE` below) need a light-on-dark treatment.
 */
const BACKGROUND_CLASS: Partial<Record<string, string>> = {
  'partners-benefits': 'bg-gradient-to-b from-[#f9f8f3] to-[#f5fdf9]',
  'seed-financing-package': 'bg-gradient-to-b from-[#f9f8f3] to-[#f5fdf9]',
  'exclusive-workspaces': 'bg-dkgreen',
};

// fundraising-advantage's header text colors, confirmed from Figma, are not
// the generic dkgreen-eyebrow/navy-title default this component otherwise
// uses: navy eyebrow, dkgreen title, teal subtitle. Benefits' membership-
// benefits section uses a different scheme in Figma (green eyebrow) and is
// left on the default below, not touched here.
//
// `body`/`cardsHeading`/`priceLine`/`closingLine` are optional on this map -
// only a dark-background section (currently just exclusive-workspaces) needs
// to override them; every light-background section falls through to the
// same dark-text defaults it always had. Without these, a Super Admin
// filling in any of those four fields on a dark `card-grid` section through
// the CMS would get invisible dark-on-dark text - the same class of bug this
// map already exists to prevent for eyebrow/title/subtitle.
const HEADER_COLOR_OVERRIDE: Partial<
  Record<
    string,
    { eyebrow: string; title: string; subtitle: string; body?: string; cardsHeading?: string; priceLine?: string; closingLine?: string }
  >
> = {
  'fundraising-advantage': { eyebrow: 'text-navy', title: 'text-dkgreen', subtitle: 'text-teal' },
  // Trusted Partners' Partners Benefits carousel (FR-143) - Figma's own
  // subtitle color is pink (#ef476f), removed per MYN-215/A-125 and replaced
  // with the existing accent palette rather than reproduced.
  'partners-benefits': { eyebrow: 'text-ink', title: 'text-teal', subtitle: 'text-dkgreen' },
  // Due Diligence Platform's "exclusive-workspaces" dark band (MYN-269) -
  // yellow eyebrow on dark, matching `cta-banner`'s own dark-variant eyebrow
  // fallback; white title/subtitle/body/cardsHeading/priceLine/closingLine so
  // every text node reads against `bg-dkgreen`, not just the ones this
  // section happens to use today.
  'exclusive-workspaces': {
    eyebrow: 'text-yellow',
    title: 'text-white',
    subtitle: 'text-white/80',
    body: 'text-white/80',
    cardsHeading: 'text-white',
    priceLine: 'text-white',
    closingLine: 'text-white/80',
  },
};

/**
 * Sections whose cards use the icon-in-circle + title-row + body treatment
 * (Trusted Partners' Partners Benefits, FR-143) instead of `GridCard`'s
 * photo-on-top default - independent of grid layout, which both styles share.
 */
const ICON_CARD_KEYS = new Set(['partners-benefits']);

/**
 * Per-band layout overrides (FR-308). Three facts about one band, read once
 * rather than tested separately where each is used - the case lists in this
 * file have drifted before.
 *
 * Fundraising Support's "exclusive workspaces" band used to give its image
 * half the row (`col-span-2` of 4) and its two cards a quarter each. UAT
 * asked for the reverse: the text wider, the image a narrow, height-matched
 * strip, and no hover on cards that are not clickable in the first place.
 */
const CARD_BAND_LAYOUT: Partial<Record<string, { card: string; filler: string; flatCards: boolean }>> = {
  'exclusive-workspaces': { card: 'lg:col-span-2', filler: 'lg:col-span-1 lg:h-full', flatCards: true },
};

function GridCard({
  card,
  image,
  highlighted,
  flat,
  gridClassName,
}: {
  card: CardItem;
  image: string | undefined;
  flat: boolean;
  highlighted: boolean;
  /**
   * The band's own grid-column span (FR-308), if any, applied to this same
   * element - not a wrapper. A wrapper div would sit between this card and
   * the grid, which stretches to the row's tallest card by default
   * (`align-items: stretch`); the wrapper would pick up that stretch instead
   * of the card inside it, leaving every card back at its own content
   * height. Keeping `GridCard`'s own div as the direct grid child is what
   * every section's equal-height cards have always relied on.
   */
  gridClassName?: string;
}) {
  // `flat` drops the hover treatment entirely rather than overriding it, so a
  // flat card carries no hover classes a later change could resurrect.
  const surface = highlighted ? 'bg-slate' : 'bg-white';
  const hover = flat
    ? ''
    : `transition-colors hover:shadow-md ${highlighted ? 'hover:bg-slate/80' : 'hover:bg-slate/10'}`;
  return (
    <div
      className={`flex flex-col gap-3 rounded-2xl border border-slate p-6 ${surface} ${hover} ${gridClassName ?? ''}`.trim()}
    >
      {/* aspect-[683/270.78] matches Figma's own card-image proportions
        (measured from its generated code) - a fixed height looked
        fine at 4 columns but badly letterboxed once this section's
        cards became wider at 2 columns. */}
      {image && <img src={image} alt="" className="aspect-[683/270.78] w-full rounded-lg object-cover" />}
      <p className="text-p2 font-semibold text-navy">{card.title}</p>
      <p className={PUBLIC_BODY_CLASS}>{card.body}</p>
    </div>
  );
}

/**
 * Icon-card style (Trusted Partners' Partners Benefits, FR-143) - an
 * icon-in-circle beside the title, body below, distinct from `GridCard`'s
 * photo-on-top treatment. Sits in the same static grid as any other
 * `card-grid` usage - this is a card-style difference, not a layout one.
 */
function IconCard({ card }: { card: CardItem }) {
  const icon = card.icon ? CARD_ICON_BY_KEY[card.icon] : undefined;
  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-slate/25 bg-white p-6 shadow-[0_6px_18px_0_rgba(0,64,92,0.03)]">
      <div className="flex items-center gap-4">
        {icon && (
          <div className="bg-dkgreen/10 flex size-16 shrink-0 items-center justify-center rounded-2xl">
            <img src={icon} alt="" className="size-8" />
          </div>
        )}
        <p className="text-p2 font-semibold text-navy">{card.title}</p>
      </div>
      <p className={PUBLIC_BODY_CLASS}>{card.body}</p>
    </div>
  );
}

/**
 * Card grid section (FR-110, FR-121, FR-143) - Home's "Everything You Get",
 * Benefits' "Free Membership Benefits", and Trusted Partners' "Partners
 * Benefits" (icon-card style, 3-column grid - see `ICON_CARD_KEYS`/
 * `LG_COLUMNS` above; CORRECTED 2026-10-08 away from a horizontally-
 * scrolling carousel per trusted_p_ch.docx's "visible at a glance... listed
 * like the membership benefits" instruction, received after `02-design.md`
 * §22 was signed off). A card marked `highlighted` gets a persistent accent
 * border, distinct from every card's own hover state, per the resolved
 * design decision (docs/specs/02-design.md §17): the two never mean the
 * same thing, so they must never look the same.
 */
export function CardGridRenderer({ section }: SectionRendererProps) {
  const c = section.content as unknown as CardGridContent;
  // Only a band that carries an offer placeholder or CTA asks for the offer, so
  // Home, Benefits and Partners never make the request. Until it is known (or
  // when it failed) the placeholders are removed and the price line dropped.
  const needsOffer = usesOfferTokens(
    c.subtitle,
    c.cardsHeading,
    c.priceLine,
    c.closingLine,
    ...(c.body ?? []),
    ...(c.cards ?? []).flatMap((card) => [card.title, card.body]),
  );
  const { data: offer } = useIssueReportOffer({ enabled: needsOffer });
  // A band with no placeholder is left exactly as written, so a lone brace in
  // its CMS text is never stripped.
  const fill = (text: string) => (needsOffer ? fillOfferTokens(text, offer) : text);
  const priceLine = fillOfferLine(c.priceLine, offer);
  const lgCols = LG_COLUMNS[section.key] ?? 4;
  const headerColors = HEADER_COLOR_OVERRIDE[section.key];
  const eyebrowClass = headerColors?.eyebrow ?? 'text-dkgreen';
  const titleClass = headerColors?.title ?? 'text-text-primary';
  const subtitleClass = headerColors?.subtitle ?? 'text-text-secondary';
  const bodyClass = headerColors?.body ?? PUBLIC_BODY_COLOR;
  const cardsHeadingClass = headerColors?.cardsHeading ?? 'text-navy';
  const priceLineClass = headerColors?.priceLine ?? 'text-navy';
  const closingLineClass = headerColors?.closingLine ?? 'text-text-secondary';
  // A Super Admin is meant to pick ONE "best" card (docs/specs/02-design.md
  // §19's resolved decision) - if more than one card is ever marked
  // highlighted, only the first (by card order) actually renders the
  // treatment, rather than showing every checked card as "the pick".
  const highlightedIndex = c.cards?.findIndex((card) => card.highlighted) ?? -1;
  // Falls back to this section's default per-card image when the CMS has none set - see default-images.ts.
  const defaultCardImages = DEFAULT_CARD_GRID_IMAGES[section.key];
  const useIconCards = ICON_CARD_KEYS.has(section.key);
  // Partners Benefits' header has a photo beside the eyebrow/title/subtitle
  // text (Figma) - every other card-grid header is text-only, so this is
  // additive, not a layout change for existing sections.
  const headerImage = DEFAULT_CARD_GRID_HEADER_IMAGE[section.key];
  const backgroundClass = BACKGROUND_CLASS[section.key] ?? 'bg-white';
  // Fills the empty grid space beside a short card list (e.g.
  // exclusive-workspaces' 2 cards against the 4-column desktop default) -
  // see default-images.ts.
  const fillerImage = DEFAULT_CARD_GRID_FILLER_IMAGE[section.key];
  const band = CARD_BAND_LAYOUT[section.key];

  return (
    <section className={`w-full ${backgroundClass}`}>
      {/* A band with a header image starts tighter at the top and sits closer
        to its own cards (MYN-327, FR-319): the image used to be tall enough
        to dominate the header row, and centring a short text block against it
        left a large empty gap above the eyebrow and another below the image,
        reading as two disconnected halves rather than one section. */}
      <div
        className={`mx-auto flex w-full max-w-328 flex-col px-4 ${
          headerImage ? 'gap-6 pt-10 pb-16' : 'gap-8 py-16'
        }`}
      >
        <div className={headerImage ? 'flex items-center gap-10' : 'flex flex-col gap-2'}>
          <div className="flex flex-1 flex-col gap-2">
            {c.eyebrow && <p className={`text-p6 font-semibold uppercase ${eyebrowClass}`}>{c.eyebrow}</p>}
            {c.title && <h2 className={`text-h2 ${titleClass}`}>{c.title}</h2>}
            {c.subtitle && <p className={`text-p4 ${subtitleClass}`}>{fill(c.subtitle)}</p>}
            {c.body?.map((paragraph, i) => (
              <p key={i} className={`text-p4 ${bodyClass}`}>
                {fill(paragraph)}
              </p>
            ))}
          </div>
          {/* Was w-[536px]/h-[388px], matching Figma's own header-image
            container. MYN-327 (FR-319) cut it down: at 388px the image set
            the whole row's height, so the short text beside it was centred
            against empty space. `object-contain` rather than `cover` because
            this is a cut-out photo - cropping a shorter box cut the people
            off. The section's own background is untouched. */}
          {headerImage && (
            <img
              src={headerImage}
              alt=""
              className="hidden h-64 w-auto max-w-md shrink-0 object-contain lg:block"
            />
          )}
        </div>
        {c.cardsHeading && <p className={`text-p3 font-semibold ${cardsHeadingClass}`}>{fill(c.cardsHeading)}</p>}
        <div className={`grid grid-cols-1 gap-6 sm:grid-cols-2 ${LG_COLUMNS_CLASS[lgCols]}`}>
          {c.cards?.map((card, i) =>
            useIconCards ? (
              <IconCard key={i} card={{ ...card, title: fill(card.title), body: fill(card.body) }} />
            ) : (
              <GridCard
                key={i}
                card={{ ...card, title: fill(card.title), body: fill(card.body) }}
                image={imageOrDefault(card.image, defaultCardImages?.[i])}
                highlighted={i === highlightedIndex}
                flat={band?.flatCards ?? false}
                gridClassName={band?.card}
              />
            ),
          )}
          {fillerImage && (
            <img
              src={fillerImage}
              alt=""
              className={`hidden rounded-2xl object-cover lg:block ${band?.filler ?? 'lg:col-span-2'}`}
            />
          )}
        </div>
        {priceLine && <p className={`text-p3 font-semibold ${priceLineClass}`}>{priceLine}</p>}
        {c.closingLine && <p className={`text-p4 ${closingLineClass}`}>{fill(c.closingLine)}</p>}
        {c.ctaLabel && (
          <SectionCta
            label={c.ctaLabel}
            url={c.ctaUrl}
            offerCta={c.offerCta}
            openInNewTab={c.ctaOpensInNewTab}
          />
        )}
      </div>
    </section>
  );
}
